import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { RangementView } from '@/components/admin/protocoles/rangement/rangement-view';
import { AnalystSection } from '@/components/admin/protocoles/reading/analyst-section';
import { ProtocolViewSwitch } from '@/components/admin/protocoles/reading/protocol-view-switch';
import { StepEditor } from '@/components/admin/protocoles/step/step-editor';
import { ProtocolTimeline } from '@/components/admin/protocoles/timeline/protocol-timeline';
import type { VersionedFieldsBinding } from '@/components/admin/versioned/versioned-editor';
import { duplicateStep } from '@/lib/duplication';
import type { ProtocolDraft } from '@/lib/protocol-draft';
import {
  insertStep,
  moveStep,
  removeStep,
  replaceStep,
  selectionAfterRemoval,
} from '@/lib/protocol-draft';
import type { ProtocolEditorView } from '@/lib/protocol-search';
import type { CriterionCard, ProtocolStepDraft } from '@matter_extractor/shared';
import {
  PROTOCOL_DESCRIPTION_MAX,
  PROTOCOL_NAME_MAX,
  analystSectionComplete,
  analystSectionGaps,
} from '@matter_extractor/shared';
import { TextField } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * Le formulaire du protocole : l'identité, la frise, l'étape sélectionnée
 * (refonte modulaire §7.1; maquette `editeur-protocole.html`, écrans 2 et 3).
 *
 * §7.1 fixes the order, and it is a reading order rather than a layout: « les
 * champs nom et description ; la frise des étapes ; le contenu de l'étape
 * sélectionnée, l'étape 1 par défaut ». Everything above it - the head, the two
 * pastilles, the state of the draft, the one button, the drawer - is the
 * versioned layer; everything inside the last two blocks belongs to the frise
 * and to the step editor, which are built against `contracts.ts` by two other
 * hands.
 *
 * ## What this file actually owns: the EDITS
 *
 * The two surfaces are CONTROLLED (`contracts.ts`): they render the draft and
 * the selection and ask for a change through a callback. So the mapping between
 * « the frise dropped a step at index 3 » and « here is the next draft » lives
 * here, and it is eight calls into `lib/protocol-draft.ts` - pure, tested
 * without a render, and the same functions the frise's own contract quotes for
 * its `onMove`.
 *
 * ## An insertion SELECTS what it inserted
 *
 * §7.1 has the frise insert « entre deux, au début ou à la fin », and the step
 * that appears is empty: leaving the selection where it was would show a card
 * appear and the panel below it stay on another step, which reads as a click
 * that half worked. The new step is the one at the slot in the resulting list,
 * which is exactly what `insertStep` puts there.
 *
 * ## The two fields are the criterion's, in two columns
 *
 * The name commits on BLUR through `binding.onNameCommit`, because a rename is
 * its own write (§4) and not part of the draft autosave. The description rides
 * the autosave like everything else. Two columns from `md`, as the maquette's
 * `.ident` draws them, and one on a phone - `admin-responsive.spec.ts` holds
 * every admin screen at zero lateral overflow.
 *
 * ## Under them, the switch of #154 and the section of #196
 *
 * « Conduite de l'entretien », the frise and the step as they always were, or
 * « Analyste », the section in their place (analyste.md §3.5): the levels of the
 * objectives, the numbers and the two readings on the left, « Ce que la passe N
 * recevra » on the right. The position is the URL's (N47), so it arrives here as
 * `view` and leaves as `onSelectView`. The section is two more fields of the draft
 * written through `onDraft` like the description - `analyst`, and the `analystLevel`
 * of each objective, inside `steps` - and it is handed the draft's steps, which its
 * readings cite objectives of by `@` (#158). The reading of before #196,
 * `analystReading`, is read there too, folded, and never written but by « Reprendre ».
 * The SELECTION of a step survives a trip to the section: it lives in
 * `protocol-editor.tsx`, above the views.
 *
 * A third position, « Rangement » (scenario-succes.md §4, #177), holds the
 * objectives the rangeur files the conversation onto and its consigne: one more
 * field of the draft, `rangement`, written through `onDraft` in one piece.
 *
 * And all three views stay MOUNTED, the other ones `hidden` (review of !474): the
 * frise keeps its view, its zoom and its three switches in internal state, and
 * the panels their folded parts - a switch that unmounted a view would hand back
 * a frise reset to its defaults after every glance at the section. The frise
 * re-measures itself when it shows again: its layout effect runs after every
 * render, and its `ResizeObserver` sees the track come back from zero.
 */

export interface ProtocolEditorFieldsProps {
  /** What the versioned layer hands a form: the draft, the read-only state, the two writes. */
  binding: VersionedFieldsBinding<ProtocolDraft>;
  /** The step whose content is shown - always one of the draft's. */
  selectedStepId: string;
  onSelect: (stepId: string) => void;
  /** The criteria library, for the names and pointers the step editor renders. */
  criteria: readonly CriterionCard[];
  /** True once that library has been READ - an unread one accuses nobody. */
  criteriaRead: boolean;
  /** The position of the switch the URL names (N47). */
  view: ProtocolEditorView;
  /** Where the switch goes: the route writes `?vue=`. */
  onSelectView: (view: ProtocolEditorView) => void;
}

export function ProtocolEditorFields({
  binding,
  selectedStepId,
  onSelect,
  criteria,
  criteriaRead,
  view,
  onSelectView,
}: ProtocolEditorFieldsProps) {
  /**
   * The step whose name field a duplication just asked for (§4.2).
   *
   * It lives here and not in the step editor because only this file knows a
   * selection came from « Dupliquer » rather than from a click on the frise.
   * The editor clears it through `onNameFocused` as soon as the field has the
   * caret, so a request cannot outlive its gesture.
   */
  const [nameFocusId, setNameFocusId] = useState<string | null>(null);

  const { draft, readOnly, nameError, onDraft, onNameCommit } = binding;

  /** One draft with a new list of steps - every edit below goes through here. */
  const withSteps = (steps: readonly ProtocolStepDraft[]) => onDraft({ ...draft, steps });

  return (
    <div className="min-w-0 space-y-7">
      <section className="grid gap-5 md:grid-cols-2">
        <div className="min-w-0">
          {/* The hint or the refusal, never neither: a field whose rule is only
              painted beside it has no rule for whoever cannot see it. The package
              field points its describedby at whichever it shows. */}
          <TextField
            label="Nom"
            data-testid="protocole-nom"
            value={draft.name}
            readOnly={readOnly}
            required
            aria-required="true"
            maxLength={PROTOCOL_NAME_MAX}
            placeholder="Un nom unique parmi les protocoles actifs"
            onChange={(event) => onDraft({ ...draft, name: event.currentTarget.value })}
            onBlur={onNameCommit}
            help="Unique parmi les protocoles actifs. Renommer ne crée pas de version."
            status={nameError === null ? 'default' : 'error'}
            message={nameError ?? undefined}
          />
        </div>

        <div className="min-w-0">
          <TextField
            label="Description courte"
            data-testid={PROTOCOL_TEST_IDS.description}
            value={draft.description}
            readOnly={readOnly}
            required
            aria-required="true"
            maxLength={PROTOCOL_DESCRIPTION_MAX}
            placeholder="Ce que ce protocole cherche à obtenir, en une phrase"
            onChange={(event) => onDraft({ ...draft, description: event.currentTarget.value })}
            help="Celle des cartes et de la page de choix d’un lien."
          />
        </div>
      </section>

      <div className="flex min-w-0 flex-col gap-5">
        <ProtocolViewSwitch
          view={view}
          // The yellow mark: a level no objective carries, or an empty reading (§3.5).
          incomplete={!analystSectionComplete(analystSectionGaps(draft.steps, draft.analyst))}
          onSelect={onSelectView}
        />

        <div hidden={view !== 'lecture'} className="min-w-0">
          <AnalystSection
            analyst={draft.analyst}
            previousReading={draft.analystReading}
            steps={draft.steps}
            readOnly={readOnly}
            onAnalyst={(analyst) => onDraft({ ...draft, analyst })}
            onSteps={withSteps}
          />
        </div>
        <div hidden={view !== 'rangement'} className="min-w-0">
          <RangementView
            rangement={draft.rangement}
            steps={draft.steps}
            readOnly={readOnly}
            onChange={(rangement) => onDraft({ ...draft, rangement })}
          />
        </div>
        <div hidden={view !== 'conduite'} className="min-w-0">
          <div className="min-w-0 space-y-7">
            <ProtocolTimeline
              steps={draft.steps}
              selectedStepId={selectedStepId}
              readOnly={readOnly}
              visible={view === 'conduite'}
              onSelect={onSelect}
              onInsert={(index) => {
                const steps = insertStep(draft.steps, index);
                withSteps(steps);
                // The step the slot produced, and never `steps.at(-1)`: an insertion
                // in the middle is the whole point of the « + » between two cards.
                const inserted = steps[Math.max(0, Math.min(index, steps.length - 1))];
                if (inserted !== undefined) onSelect(inserted.id);
              }}
              onMove={(fromIndex, toIndex) => withSteps(moveStep(draft.steps, fromIndex, toIndex))}
              onRemoveEmpty={(stepId) => {
                withSteps(removeStep(draft.steps, stepId));
                onSelect(selectionAfterRemoval(draft.steps, stepId, selectedStepId));
              }}
            />

            <StepEditor
              steps={draft.steps}
              stepId={selectedStepId}
              criteria={criteria}
              criteriaRead={criteriaRead}
              readOnly={readOnly}
              nameFocusId={nameFocusId}
              onNameFocused={() => setNameFocusId(null)}
              onChangeStep={(next) => withSteps(replaceStep(draft.steps, next))}
              onMoveStep={(by) => {
                const from = draft.steps.findIndex((step) => step.id === selectedStepId);
                if (from < 0) return;
                withSteps(moveStep(draft.steps, from, from + by));
              }}
              onDeleteStep={() => {
                withSteps(removeStep(draft.steps, selectedStepId));
                onSelect(selectionAfterRemoval(draft.steps, selectedStepId, selectedStepId));
              }}
              // §4.2: the copy lands just after its source, the frise SELECTS it,
              // and its name field is selected with it. The three fit in three lines
              // because `duplicateStep` answers the next list AND the copy's id.
              onDuplicateStep={() => {
                const done = duplicateStep(draft.steps, selectedStepId);
                if (done === null) return;
                withSteps(done.list);
                onSelect(done.cloneId);
                setNameFocusId(done.cloneId);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
